// What has moved on an action list since the page opened, so an operator working down the
// list sees each change land rather than the list shifting under them (Noaa, 29 Oct 2026).
// Held in memory only: a reload starts again from what it loads.
import { daysSince, type NextAction } from "../api/hooks/useProjectAgentStatus";
import { formatShortDay } from "./attributeFormat";
import { statusLabel } from "./nextActions";

/** "new" appeared since the page opened; "closed" was open then and is closed now; "gone"
 * was open then and is no longer on the list (another owner, or removed); "moved" anything
 * else that changed: its date, its status, or reopened. */
export type ChangeKind = "new" | "closed" | "gone" | "moved";

export interface ActionChange {
  kind: ChangeKind;
  /** What the row says moved: "Date 28 Oct → 4 Nov", "To send → Waiting", "Closed". */
  notes: string[];
  dateMoved: boolean;
}

const day = (iso: string | null | undefined): string => (iso ? formatShortDay(iso) : "no date");

/** How ``now`` differs from ``was``, the same action when the page opened; null when it
 * has not changed, or was closed then and has since aged off the list. */
export const actionChange = (
  was: NextAction | undefined,
  now: NextAction | undefined,
): ActionChange | null => {
  if (!was) return now ? { kind: "new", notes: ["New"], dateMoved: false } : null;
  if (!now)
    return was.closed
      ? null
      : { kind: "gone", notes: ["No longer on this list"], dateMoved: false };
  if (!was.closed && now.closed) return { kind: "closed", notes: ["Closed"], dateMoved: false };
  if (was.closed && !now.closed) return { kind: "moved", notes: ["Reopened"], dateMoved: false };
  const dateMoved = (was.due ?? "") !== (now.due ?? "");
  const notes = [
    ...(dateMoved ? [`Date ${day(was.due)} → ${day(now.due)}`] : []),
    ...(statusLabel(was) !== statusLabel(now)
      ? [`${statusLabel(was) || "No status"} → ${statusLabel(now) || "no status"}`]
      : []),
  ];
  return notes.length > 0 ? { kind: "moved", notes, dateMoved } : null;
};

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** "3 closed, 1 no longer on this list, 2 new, 4 dates moved, 1 other change"; "" with
 * nothing changed. */
export const changeTally = (changes: Iterable<ActionChange>): string => {
  let closed = 0;
  let gone = 0;
  let added = 0;
  let dates = 0;
  let other = 0;
  for (const change of changes) {
    if (change.kind === "closed") closed += 1;
    else if (change.kind === "gone") gone += 1;
    else if (change.kind === "new") added += 1;
    else if (change.dateMoved) dates += 1;
    else other += 1;
  }
  return [
    closed > 0 && `${closed} closed`,
    gone > 0 && `${gone} no longer on this list`,
    added > 0 && `${added} new`,
    dates > 0 && plural(dates, "date moved", "dates moved"),
    other > 0 && plural(other, "other change", "other changes"),
  ]
    .filter(Boolean)
    .join(", ");
};

/** How long a row says its agent moved an operator's date, from the agent's move. */
const OVERRIDE_SHOWN_DAYS = 14;

export interface AgentOverride {
  agent: string;
  /** The operator's date and the agent's. */
  from: string;
  to: string;
  note: string;
}

/** The agent moved an open action's date after an operator last did: the operator's push
 * or answer did not stand. Read from the action's own date log, so it shows on every page
 * and after a reload, until another move or the window passes. A closed action cannot be
 * reopened by an agent, so a resolve has no such case. */
export const agentOverride = (action: NextAction): AgentOverride | null => {
  const log = action.due_log ?? [];
  const last = log[log.length - 1];
  const before = log[log.length - 2];
  if (action.closed || !last || !before) return null;
  const agent = /^agent:([a-z_]+)/.exec(last.by)?.[1];
  // "set" is the agent choosing a date; a new contact cycle after a reply is not a
  // disagreement with the operator's.
  if (!agent || last.kind !== "set" || before.by.startsWith("agent:")) return null;
  if ((daysSince(last.at.slice(0, 10)) ?? 0) > OVERRIDE_SHOWN_DAYS) return null;
  return { agent, from: before.to, to: last.to, note: last.note };
};
